import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7200/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('silogate_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Warm-up: ping backend to wake the DB from auto-pause ──
// Free-tier Azure SQL serverless pauses after ~1 hour idle and takes 10–15s to
// resume. We track the last successful ping and re-warm if it's been a while.
// Pass force=true (e.g. on login) to bypass the cache and ping immediately.
let _lastWarmedAt = 0;
const WARM_VALID_MS = 50 * 60 * 1000;  // 50 min — slightly under the 60 min auto-pause window
export const warmUpBackend = async (force = false) => {
  if (!force && Date.now() - _lastWarmedAt < WARM_VALID_MS) return;
  try {
    await axios.get(API_URL.replace('/api', '') + '/api/health', { timeout: 20000 });
    _lastWarmedAt = Date.now();
  } catch {
    await new Promise(r => setTimeout(r, 3000));
    try {
      await axios.get(API_URL.replace('/api', '') + '/api/health', { timeout: 20000 });
      _lastWarmedAt = Date.now();
    } catch {}
  }
};
warmUpBackend();

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('silogate_token');
      localStorage.removeItem('silogate_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  microsoftLogin: (idToken) => api.post('/auth/microsoft', { idToken }),
  me: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
};

// ── Dashboard ──
export const dashboardApi = {
  get: (fy) => api.get('/dashboard', { params: { financialYear: fy } }),
  notifications: () => api.get('/dashboard/notifications'),
  markRead: (id) => api.put(`/dashboard/notifications/${id}/read`),
};

// ── Strategic Objectives ──
export const objectivesApi = {
  getAll: (fy) => api.get('/strategicobjectives', { params: { financialYear: fy } }),
  get: (id) => api.get(`/strategicobjectives/${id}`),
  getUsedReferences: () => api.get('/strategicobjectives/used-references'),
  create: (data) => api.post('/strategicobjectives', data),
  update: (id, data) => api.put(`/strategicobjectives/${id}`, data),
  delete: (id) => api.delete(`/strategicobjectives/${id}`),
};

// ── Programmes (Initiatives) ──
export const programmesApi = {
  getAll: (params) => api.get('/initiatives', { params }),
  get: (id) => api.get(`/initiatives/${id}`),
  getUsedReferences: () => api.get('/initiatives/used-references'),
  create: (data) => api.post('/initiatives', data),
  update: (id, data) => api.put(`/initiatives/${id}`, data),
  delete: (id) => api.delete(`/initiatives/${id}`),
};
export const initiativesApi = programmesApi;

// ── Clusters ──
export const clustersApi = {
  getAll: () => api.get('/clusters'),
  get: (id) => api.get(`/clusters/${id}`),
  create: (data) => api.post('/clusters', data),
  update: (id, data) => api.put(`/clusters/${id}`, data),
  delete: (id) => api.delete(`/clusters/${id}`),
};

// ── Research Themes (V3.1: admin-managed lookup for R&D Projects) ──
export const researchThemesApi = {
  getAll: (activeOnly) => api.get('/researchthemes', { params: { activeOnly } }),
  get: (id) => api.get(`/researchthemes/${id}`),
  create: (data) => api.post('/researchthemes', data),
  update: (id, data) => api.put(`/researchthemes/${id}`, data),
  delete: (id) => api.delete(`/researchthemes/${id}`),
};

// ── Motivation Topics (V3.1: admin-managed list for R&D Research Motivation form) ──
export const motivationTopicsApi = {
  getAll: (activeOnly, category) => api.get('/motivationtopics', { params: { activeOnly, category } }),
  get: (id) => api.get(`/motivationtopics/${id}`),
  create: (data) => api.post('/motivationtopics', data),
  update: (id, data) => api.put(`/motivationtopics/${id}`, data),
  delete: (id) => api.delete(`/motivationtopics/${id}`),
};

// ── Divisions ──
export const divisionsApi = {
  getAll: (clusterId) => api.get('/divisions', { params: { clusterId } }),
  get: (id) => api.get(`/divisions/${id}`),
  create: (data) => api.post('/divisions', data),
  update: (id, data) => api.put(`/divisions/${id}`, data),
  delete: (id) => api.delete(`/divisions/${id}`),
};

// ── V3: Financial Years ──
export const financialYearsApi = {
  getAll: () => api.get('/financialyears'),
  get: (id) => api.get(`/financialyears/${id}`),
  getCurrent: () => api.get('/financialyears/current'),
  create: (data) => api.post('/financialyears', data),
  update: (id, data) => api.put(`/financialyears/${id}`, data),
  setCurrent: (id) => api.put(`/financialyears/${id}/set-current`),
  lock: (id) => api.put(`/financialyears/${id}/lock`),
  unlock: (id) => api.put(`/financialyears/${id}/unlock`),
  delete: (id) => api.delete(`/financialyears/${id}`),
  getDeadlines: (yearStart) => api.get(`/financialyears/${yearStart}/deadlines`),
};

// ── Projects ──
export const projectsApi = {
  getAll: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getMilestones: (pid) => api.get(`/projects/${pid}/milestones`),
  addMilestone: (pid, data) => api.post(`/projects/${pid}/milestones`, data),
  updateMilestone: (id, data) => api.put(`/projects/milestones/${id}`, data),
  deleteMilestone: (id) => api.delete(`/projects/milestones/${id}`),
  getBudget: (pid) => api.get(`/projects/${pid}/budget`),
  addBudgetLine: (pid, data) => api.post(`/projects/${pid}/budget`, data),
  updateBudgetLine: (id, data) => api.put(`/projects/budget/${id}`, data),
  deleteBudgetLine: (id) => api.delete(`/projects/budget/${id}`),
  getGrants: (pid) => api.get(`/projects/${pid}/grants`),
  addGrant: (pid, data) => api.post(`/projects/${pid}/grants`, data),
  updateGrant: (id, data) => api.put(`/projects/grants/${id}`, data),
  deleteGrant: (id) => api.delete(`/projects/grants/${id}`),
  addStageGate: (pid, data) => api.post(`/projects/${pid}/stagegates`, data),
  updateStageGate: (id, data) => api.put(`/projects/stagegates/${id}`, data),
  getProjectSOs: (pid) => api.get(`/projects/${pid}/strategic-objectives`),
  setProjectSOs: (pid, soIds) => api.put(`/projects/${pid}/strategic-objectives`, soIds),
  getProjectProgrammes: (pid) => api.get(`/projects/${pid}/programmes`),
  setProjectProgrammes: (pid, progIds) => api.put(`/projects/${pid}/programmes`, progIds),
  recalculateProgress: (pid) => api.post(`/projects/${pid}/recalculate-progress`),

  // ── Phase 1: KPI linkage ──
  getProjectKpis: (pid) => api.get(`/projects/${pid}/kpis`),
  setProjectKpis: (pid, kpiIds) => api.put(`/projects/${pid}/kpis`, kpiIds),

  // ── Phase 1: Commercial extensions (Option B) ──
  getExtensions: (parentId) => api.get(`/projects/${parentId}/extensions`),
  getAwardedRollup: (parentId) => api.get(`/projects/${parentId}/awarded-rollup`),

  // ── Phase 2: Multi-year plan lifecycle ──
  getYearPlans: (pid) => api.get(`/projects/${pid}/year-plans`),
  getYearPlan: (pid, yr) => api.get(`/projects/${pid}/year-plans/${yr}`),
  createYearPlan: (pid, data) => api.post(`/projects/${pid}/year-plans`, data),
  updateYearPlan: (pid, yr, data) => api.put(`/projects/${pid}/year-plans/${yr}`, data),
  deleteYearPlan: (pid, yr) => api.delete(`/projects/${pid}/year-plans/${yr}`),
  requestYearActivation: (pid, yr, motivation) => api.post(`/projects/${pid}/year-plans/${yr}/request-activation`, { motivation }),
  approveYearActivation: (pid, yr) => api.post(`/projects/${pid}/year-plans/${yr}/approve-activation`),
  rejectYearActivation: (pid, yr, notes) => api.post(`/projects/${pid}/year-plans/${yr}/reject-activation`, { notes }),
  requestYearClosure: (pid, yr, notes) => api.post(`/projects/${pid}/year-plans/${yr}/request-closure`, { notes }),
  approveYearClosure: (pid, yr) => api.post(`/projects/${pid}/year-plans/${yr}/approve-closure`),

  // ── Phase 2: Resources ──
  getResources: (pid, params) => api.get(`/projects/${pid}/resources`, { params }),
  createResource: (pid, data) => api.post(`/projects/${pid}/resources`, data),
  updateResource: (pid, id, data) => api.put(`/projects/${pid}/resources/${id}`, data),
  deleteResource: (pid, id) => api.delete(`/projects/${pid}/resources/${id}`),

  // ── Phase 3: Running cost lines ──
  getRunningCosts: (pid, params) => api.get(`/projects/${pid}/running-costs`, { params }),
  createRunningCost: (pid, data) => api.post(`/projects/${pid}/running-costs`, data),
  updateRunningCost: (pid, id, data) => api.put(`/projects/${pid}/running-costs/${id}`, data),
  deleteRunningCost: (pid, id) => api.delete(`/projects/${pid}/running-costs/${id}`),
};

// ── Phase 1: Admin-managed role rate card ──
export const roleRatesApi = {
  getAll: (params) => api.get('/rolerates', { params }),
  get: (id) => api.get(`/rolerates/${id}`),
  resolve: (code, asOf) => api.get('/rolerates/resolve', { params: { code, asOf } }),
  create: (data) => api.post('/rolerates', data),
  update: (id, data) => api.put(`/rolerates/${id}`, data),
  delete: (id) => api.delete(`/rolerates/${id}`),
};

// ── KPIs ──
export const kpisApi = {
  getAll: (params) => api.get('/kpis', { params }),
  get: (id) => api.get(`/kpis/${id}`),
  getUsedReferences: () => api.get('/kpis/used-references'),
  create: (data) => api.post('/kpis', data),
  update: (id, data) => api.put(`/kpis/${id}`, data),
  delete: (id) => api.delete(`/kpis/${id}`),
  getActuals: (kpiId, fy) => api.get(`/kpis/${kpiId}/actuals`, { params: { financialYear: fy } }),
  addActual: (kpiId, data) => api.post(`/kpis/${kpiId}/actuals`, data),
  updateActual: (id, data) => api.put(`/kpis/actuals/${id}`, data),
  approveActual: (id) => api.put(`/kpis/actuals/${id}/approve`),
  rejectActual: (id, reason) => api.put(`/kpis/actuals/${id}/reject`, reason),
};

// ── Risks ──
export const risksApi = {
  getAll: (params) => api.get('/risks', { params }),
  get: (id) => api.get(`/risks/${id}`),
  create: (data) => api.post('/risks', data),
  update: (id, data) => api.put(`/risks/${id}`, data),
  delete: (id) => api.delete(`/risks/${id}`),
  heatmap: () => api.get('/risks/heatmap'),
  getControls: (riskId) => api.get(`/risks/${riskId}/controls`),
  addControl: (riskId, data) => api.post(`/risks/${riskId}/controls`, data),
  updateControl: (id, data) => api.put(`/risks/controls/${id}`, data),
  deleteControl: (id) => api.delete(`/risks/controls/${id}`),
  getTreatments: (riskId) => api.get(`/risks/${riskId}/treatments`),
  addTreatment: (riskId, data) => api.post(`/risks/${riskId}/treatments`, data),
  updateTreatment: (id, data) => api.put(`/risks/treatments/${id}`, data),
  deleteTreatment: (id) => api.delete(`/risks/treatments/${id}`),
  addIndicator: (riskId, data) => api.post(`/risks/${riskId}/indicators`, data),
  updateIndicator: (id, data) => api.put(`/risks/indicators/${id}`, data),
  getTaxonomy: () => api.get('/risks/taxonomy'),
  addTaxonomy: (data) => api.post('/risks/taxonomy', data),
  updateTaxonomy: (id, data) => api.put(`/risks/taxonomy/${id}`, data),
  deleteTaxonomy: (id) => api.delete(`/risks/taxonomy/${id}`),
  getControlLibrary: () => api.get('/risks/control-library'),
  addControlLibraryItem: (data) => api.post('/risks/control-library', data),
};

// ── Business Plans ──
export const businessPlansApi = {
  getAll: (params) => api.get('/businessplans', { params }),
  get: (id) => api.get(`/businessplans/${id}`),
  create: (data) => api.post('/businessplans', data),
  createFromTemplate: (templateId, data) => api.post(`/businessplans/from-template/${templateId}`, data),
  update: (id, data) => api.put(`/businessplans/${id}`, data),
  delete: (id) => api.delete(`/businessplans/${id}`),
  updateStatus: (id, status) => api.put(`/businessplans/${id}/status`, { status }),
  submitForReview: (id) => api.post(`/businessplans/${id}/submit-for-review`),
  approve: (id, data) => api.post(`/businessplans/${id}/approve`, data),
  // V3: Duplicate
  duplicate: (id, data) => api.post(`/businessplans/${id}/duplicate`, data),
  // V3: Consolidation
  getConsolidation: (fy) => api.get('/businessplans/consolidation', { params: { financialYear: fy } }),
  // KPI Links (Division Scorecard)
  getLinkedKpis: (planId) => api.get(`/businessplans/${planId}/kpis`),
  setLinkedKpis: (planId, kpiIds) => api.put(`/businessplans/${planId}/kpis`, kpiIds),
  // V3.6 — Cluster plan support
  getClusterPlan: (clusterId, financialYear) => api.get('/businessplans/cluster-plan', { params: { clusterId, financialYear } }),
  getLinkedStrategicObjectives: (planId) => api.get(`/businessplans/${planId}/strategic-objectives`),
  setLinkedStrategicObjectives: (planId, soIds) => api.put(`/businessplans/${planId}/strategic-objectives`, soIds),
  getLinkedInitiatives: (planId) => api.get(`/businessplans/${planId}/initiatives`),
  setLinkedInitiatives: (planId, initIds) => api.put(`/businessplans/${planId}/initiatives`, initIds),
  // V3: Divisional Targets
  getDivisionalTargets: (planId) => api.get(`/businessplans/${planId}/divisional-targets`),
  setDivisionalTargets: (planId, targets) => api.put(`/businessplans/${planId}/divisional-targets`, targets),
  updateDivisionalTarget: (id, data) => api.put(`/businessplans/divisional-targets/${id}`, data),
  // Sections
  addSection: (planId, data) => api.post(`/businessplans/${planId}/sections`, data),
  updateSection: (id, data) => api.put(`/businessplans/sections/${id}`, data),
  deleteSection: (id) => api.delete(`/businessplans/sections/${id}`),
  moveSection: (id, direction) => api.put(`/businessplans/sections/${id}/move/${direction}`),
  // V3: Section Comments
  getComments: (sectionId) => api.get(`/businessplans/sections/${sectionId}/comments`),
  addComment: (sectionId, data) => api.post(`/businessplans/sections/${sectionId}/comments`, data),
  updateComment: (id, data) => api.put(`/businessplans/comments/${id}`, data),
  deleteComment: (id) => api.delete(`/businessplans/comments/${id}`),
  // Templates
  getTemplates: (fy) => api.get('/businessplans/templates', { params: { financialYear: fy } }),
  getApprovedTemplate: (fy) => api.get(`/businessplans/templates/approved/${fy}`),
  createTemplate: (data) => api.post('/businessplans/templates', data),
  duplicateTemplate: (templateId, data) => api.post(`/businessplans/templates/${templateId}/duplicate`, data),
  updateTemplate: (id, data) => api.put(`/businessplans/templates/${id}`, data),
  setTemplateApproved: (id) => api.put(`/businessplans/templates/${id}/set-approved`),
  deleteTemplate: (id) => api.delete(`/businessplans/templates/${id}`),
  addTemplateSection: (templateId, data) => api.post(`/businessplans/templates/${templateId}/sections`, data),
  updateTemplateSection: (id, data) => api.put(`/businessplans/templates/sections/${id}`, data),
  deleteTemplateSection: (id) => api.delete(`/businessplans/templates/sections/${id}`),
  moveTemplateSection: (id, direction) => api.put(`/businessplans/templates/sections/${id}/move/${direction}`),
  exportWord: (id) => api.get(`/businessplans/${id}/export`, { responseType: 'blob' }),
};

// ── Users ──
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  getRoles: () => api.get('/users/roles'),
  getStats: () => api.get('/users/stats'),
  create: (data) => api.post('/users', data),
  updateProfile: (id, data) => api.put(`/users/${id}`, data),
  toggleActive: (id) => api.put(`/users/${id}/toggle-active`),
  updateRoles: (id, roles) => api.put(`/users/${id}/roles`, roles),
  resetPassword: (id, data) => api.put(`/users/${id}/reset-password`, data),
  delete: (id) => api.delete(`/users/${id}`),
  bulkImport: (data) => api.post('/users/bulk-import', data),
};

// ── Evidence ──
export const evidenceApi = {
  getAll: (params) => api.get('/evidence', { params }),
  get: (id) => api.get(`/evidence/${id}`),
  create: (data) => api.post('/evidence', data),
  update: (id, data) => api.put(`/evidence/${id}`, data),
  approve: (id) => api.put(`/evidence/${id}/approve`),
  delete: (id) => api.delete(`/evidence/${id}`),
  // V3.1 — version history for a parent (KPI / Project / Risk) + FY/Quarter
  history: (params) => api.get('/evidence/history', { params }),
};

// ── Performance Reports ──
export const reportsApi = {
  getAll: (fy) => api.get('/performancereports', { params: { financialYear: fy } }),
  get: (id) => api.get(`/performancereports/${id}`),
  create: (data) => api.post('/performancereports', data),
  update: (id, data) => api.put(`/performancereports/${id}`, data),
  delete: (id) => api.delete(`/performancereports/${id}`),
  consolidate: (data) => api.post('/performancereports/consolidate', data),
};

// ── Audit Logs ──
export const auditApi = {
  getAll: (params) => api.get('/auditlogs', { params }),
  get: (id) => api.get(`/auditlogs/${id}`),
  getEntityHistory: (entityType, entityId) => api.get(`/auditlogs/entity/${entityType}/${entityId}`),
  getStats: (days) => api.get('/auditlogs/stats', { params: { days } }),
};

// ── Permissions ──
export const permissionsApi = {
  getAll: (role) => api.get('/rolepermissions', { params: { role } }),
  set: (data) => api.post('/rolepermissions', data),
  bulkSet: (data) => api.post('/rolepermissions/bulk', data),
  delete: (id) => api.delete(`/rolepermissions/${id}`),
  getModules: () => api.get('/rolepermissions/modules'),
};

// ── Workflows ──
export const workflowsApi = {
  getDefinitions: (entityType) => api.get('/workflows/definitions', { params: { entityType } }),
  getDefinition: (id) => api.get(`/workflows/definitions/${id}`),
  createDefinition: (data) => api.post('/workflows/definitions', data),
  updateDefinition: (id, data) => api.put(`/workflows/definitions/${id}`, data),
  deleteDefinition: (id) => api.delete(`/workflows/definitions/${id}`),
  addStep: (defId, data) => api.post(`/workflows/definitions/${defId}/steps`, data),
  updateStep: (id, data) => api.put(`/workflows/steps/${id}`, data),
  deleteStep: (id) => api.delete(`/workflows/steps/${id}`),
  submit: (data) => api.post('/workflows/submit', data),
  getPending: () => api.get('/workflows/pending'),
  getInstances: (params) => api.get('/workflows/instances', { params }),
  decide: (id, data) => api.post(`/workflows/instances/${id}/decide`, data),
};

// ── App Settings ──
export const settingsApi = {
  getAll: (cat) => api.get('/appsettings', { params: { category: cat } }),
  create: (data) => api.post('/appsettings', data),
  update: (id, data) => api.put(`/appsettings/${id}`, data),
  delete: (id) => api.delete(`/appsettings/${id}`),
};

// ══════════════════════════════════════
// SHEQ MODULE APIs
// ══════════════════════════════════════

export const incidentsApi = {
  getAll: (params) => api.get('/incidents', { params }),
  get: (id) => api.get(`/incidents/${id}`),
  create: (data) => api.post('/incidents', data),
  update: (id, data) => api.put(`/incidents/${id}`, data),
  submit: (id) => api.put(`/incidents/${id}/submit`),
  close: (id, comments) => api.put(`/incidents/${id}/close`, JSON.stringify(comments), { headers: { 'Content-Type': 'application/json' } }),
  getInvestigation: (incidentId) => api.get(`/incidents/${incidentId}/investigation`),
  createInvestigation: (incidentId, data) => api.post(`/incidents/${incidentId}/investigation`, data),
  updateInvestigation: (id, data) => api.put(`/incidents/investigation/${id}`, data),
  stats: (year) => api.get('/incidents/stats', { params: { year } }),
};

export const capaApi = {
  getAll: (params) => api.get('/capa', { params }),
  get: (id) => api.get(`/capa/${id}`),
  create: (data) => api.post('/capa', data),
  update: (id, data) => api.put(`/capa/${id}`, data),
  verify: (id, comments) => api.put(`/capa/${id}/verify`, JSON.stringify(comments), { headers: { 'Content-Type': 'application/json' } }),
  close: (id) => api.put(`/capa/${id}/close`),
  overdue: () => api.get('/capa/overdue'),
};

export const auditsApi = {
  getAll: (params) => api.get('/audits', { params }),
  get: (id) => api.get(`/audits/${id}`),
  create: (data) => api.post('/audits', data),
  update: (id, data) => api.put(`/audits/${id}`, data),
  delete: (id) => api.delete(`/audits/${id}`),
  addFinding: (auditId, data) => api.post(`/audits/${auditId}/findings`, data),
  updateFinding: (id, data) => api.put(`/audits/findings/${id}`, data),
  addChecklistItem: (auditId, data) => api.post(`/audits/${auditId}/checklist`, data),
  applyTemplate: (auditId, templateId) => api.post(`/audits/${auditId}/checklist/from-template/${templateId}`),
  updateChecklistItem: (id, data) => api.put(`/audits/checklist/${id}`, data),
  getTemplates: () => api.get('/audits/templates'),
  createTemplate: (data) => api.post('/audits/templates', data),
  updateTemplate: (id, data) => api.put(`/audits/templates/${id}`, data),
  addTemplateItem: (templateId, data) => api.post(`/audits/templates/${templateId}/items`, data),
  stats: () => api.get('/audits/stats'),
};

export const complianceApi = {
  getAll: (params) => api.get('/compliance', { params }),
  get: (id) => api.get(`/compliance/${id}`),
  create: (data) => api.post('/compliance', data),
  update: (id, data) => api.put(`/compliance/${id}`, data),
  delete: (id) => api.delete(`/compliance/${id}`),
  expiring: (days) => api.get('/compliance/expiring', { params: { days } }),
  calendar: (year, month) => api.get('/compliance/calendar', { params: { year, month } }),
};

export const trainingApi = {
  getCourses: () => api.get('/training/courses'),
  createCourse: (data) => api.post('/training/courses', data),
  updateCourse: (id, data) => api.put(`/training/courses/${id}`, data),
  getRecords: (params) => api.get('/training/records', { params }),
  createRecord: (data) => api.post('/training/records', data),
  updateRecord: (id, data) => api.put(`/training/records/${id}`, data),
  matrix: (divisionId) => api.get('/training/matrix', { params: { divisionId } }),
  expiring: (days) => api.get('/training/expiring', { params: { days } }),
  stats: () => api.get('/training/stats'),
};

export const sheqDashboardApi = {
  get: (year) => api.get('/sheq/sheqdashboard', { params: { year } }),
};

// ── Report Templates (V3.2 — Custom Report Builder) ──
// V3.6.1 hotfix — restored after being inadvertently dropped during the V3.6 merge.
// Each template stores a JSON configuration consumed by the builder modal.
// `getAll` returns the current user's templates plus any IsShared=true ones.
export const reportTemplatesApi = {
  getAll: (dataSource) => api.get('/reporttemplates', { params: { dataSource } }),
  get: (id) => api.get(`/reporttemplates/${id}`),
  create: (data) => api.post('/reporttemplates', data),
  update: (id, data) => api.put(`/reporttemplates/${id}`, data),
  delete: (id) => api.delete(`/reporttemplates/${id}`),
  recordRun: (id) => api.post(`/reporttemplates/${id}/run`),
};

// ── Grants (V3.3 — Grant Management) ──
// V3.6.1 hotfix — restored after being inadvertently dropped during the V3.6 merge.
// Backend controller: RdGrantsController
export const grantsApi = {
  getAll: (params) => api.get('/rdgrants', { params }),
  get: (id) => api.get(`/rdgrants/${id}`),
  create: (data) => api.post('/rdgrants', data),
  update: (id, data) => api.put(`/rdgrants/${id}`, data),
  delete: (id) => api.delete(`/rdgrants/${id}`),
  transition: (id, newStatus, notes) => api.post(`/rdgrants/${id}/transition`, { newStatus, notes }),
  linkProject: (id, projectId, data) => api.post(`/rdgrants/${id}/projects/${projectId}`, data || {}),
  unlinkProject: (id, projectId) => api.delete(`/rdgrants/${id}/projects/${projectId}`),
  getByProject: (projectId) => api.get(`/rdgrants/by-project/${projectId}`),
};

export default api;

// ── Strategic Alignment (V3.7) ──
export const strategicAlignmentApi = {
  getDashboard: (fy) => api.get('/strategicalignment/dashboard', { params: { financialYear: fy } }),
  getContributions: (params) => api.get('/strategicalignment/contributions', { params }),
  setContributions: (payload) => api.put('/strategicalignment/contributions', payload),
};